import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  EyeIcon,
  EyeSlashIcon,
  EnvelopeIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';
import { LoginRequest } from '../../types/auth';
import SEOHead from '../../components/common/SEOHead';
import { getPostLoginRedirectUrl, getRedirectMessage } from '../../utils/redirectUtils';
import ForgotPasswordModal from '../../components/auth/ForgotPasswordModal';
import { userRequiresPhone } from '../../utils/phoneGate';
import {
  getGoogleClientId,
  loadGoogleIdentityScript,
  GoogleCredentialResponse,
} from '../../utils/googleIdentity';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    login,
    loginWithGoogle,
    isAuthenticated,
    user,
    isLoading,
    error,
    clearError,
    getAndClearRedirectUrl,
  } = useAuthStore();
  const accountLocked = searchParams.get('locked') === '1';
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const [googleReady, setGoogleReady] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const googleClientId = getGoogleClientId();

  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);

  const locationRedirect = location.state?.from?.pathname;
  const storeRedirect = useAuthStore.getState().redirectAfterLogin;
  const redirectUrl = getPostLoginRedirectUrl(storeRedirect, locationRedirect);

  const goAfterLogin = (opts?: { requiresPhone?: boolean }) => {
    const needsPhone =
      opts?.requiresPhone === true || userRequiresPhone(useAuthStore.getState().user);
    if (needsPhone) {
      navigate('/auth/complete-phone', { replace: true });
      return;
    }
    const clearedRedirectUrl = getAndClearRedirectUrl();
    const message = getRedirectMessage(clearedRedirectUrl || locationRedirect);
    console.log(message);
    navigate(redirectUrl, { replace: true });
  };

  useEffect(() => {
    if (!googleClientId || !googleBtnRef.current) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await loadGoogleIdentityScript();
        if (cancelled || !window.google?.accounts?.id || !googleBtnRef.current) return;

        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: async (response: GoogleCredentialResponse) => {
            if (!response.credential) {
              setGoogleError('Không nhận được token từ Google');
              return;
            }
            try {
              clearError();
              setGoogleError(null);
              const res = await loginWithGoogle(response.credential);
              goAfterLogin({ requiresPhone: res.data?.requiresPhone });
            } catch (err) {
              console.error('Google login failed:', err);
            }
          },
        });

        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          width: 360,
          locale: 'vi',
        });
        if (!cancelled) setGoogleReady(true);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setGoogleError('Không tải được Google Sign-In. Vui lòng thử lại sau.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount once for GIS button
  }, [googleClientId]);

  if (isAuthenticated && user) {
    if (userRequiresPhone(user)) {
      return <Navigate to="/auth/complete-phone" replace />;
    }
    return <Navigate to={redirectUrl} replace />;
  }

  if (isAuthenticated && !user) {
    return <Navigate to={redirectUrl} replace />;
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = 'Email là bắt buộc';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email không hợp lệ';
    }

    if (!formData.password) {
      errors.password = 'Mật khẩu là bắt buộc';
    } else if (formData.password.length < 3) {
      errors.password = 'Mật khẩu phải có ít nhất 3 ký tự';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (error) {
      clearError();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const res = await login(formData);
      goAfterLogin({ requiresPhone: res.data?.requiresPhone });
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  return (
    <div className="min-h-screen bg-eh-keylime/40 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <SEOHead
        title="Đăng nhập | Training Center"
        description="Đăng nhập vào tài khoản Training Center để đăng ký khóa học và theo dõi tiến độ học tập của bạn."
        url="/login"
      />

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center">
          <div className="text-2xl font-semibold tracking-tight text-eh-forest-ink">M2MBA</div>
        </Link>
        <h2 className="eh-title mt-6 text-center">
          Đăng nhập tài khoản
        </h2>
        <p className="mt-2 text-center text-sm text-eh-muted">
          Hoặc{' '}
          <Link
            to="/register"
            className="font-medium text-eh-forest-ink transition-colors hover:underline"
          >
            tạo tài khoản mới
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="border border-eh-border bg-eh-cream px-4 py-8 shadow-eh-soft sm:px-10">
          {googleClientId ? (
            <div className="mb-6">
              <div
                ref={googleBtnRef}
                className="flex justify-center min-h-[44px]"
                aria-label="Đăng nhập bằng Google"
              />
              {!googleReady && !googleError && (
                <p className="mt-2 text-center text-xs text-eh-muted">Đang tải Google Sign-In…</p>
              )}
              {googleError && (
                <p className="mt-2 text-center text-sm text-m2-error">{googleError}</p>
              )}
              <div className="relative mt-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-eh-border" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-eh-cream px-2 text-eh-muted">Hoặc dùng email</span>
                </div>
              </div>
            </div>
          ) : null}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-m2-on-surface">
                Email <span className="text-m2-error">*</span>
              </label>
              <div className="mt-1 relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`block w-full appearance-none rounded-eh border py-2.5 pl-10 pr-3 sm:text-sm ${
                    validationErrors.email ? 'border-m2-error' : 'border-eh-border'
                  } bg-eh-cream placeholder:text-eh-muted focus:border-eh-forest-ink focus:outline-none focus:ring-1 focus:ring-eh-forest-ink/40`}
                  placeholder="Nhập email của bạn"
                />
                <EnvelopeIcon className="absolute left-3 top-2.5 h-5 w-5 text-eh-muted" />
              </div>
              {validationErrors.email && (
                <p className="mt-1 text-sm text-m2-error">{validationErrors.email}</p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-m2-on-surface">
                  Mật khẩu <span className="text-m2-error">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotPasswordModal(true)}
                  className="text-sm font-medium text-eh-forest-ink transition-colors hover:underline"
                >
                  Quên mật khẩu?
                </button>
              </div>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`block w-full appearance-none rounded-eh border py-2.5 pl-10 pr-12 sm:text-sm ${
                    validationErrors.password ? 'border-m2-error' : 'border-eh-border'
                  } bg-eh-cream placeholder:text-eh-muted focus:border-eh-forest-ink focus:outline-none focus:ring-1 focus:ring-eh-forest-ink/40`}
                  placeholder="Nhập mật khẩu"
                />
                <LockClosedIcon className="absolute left-3 top-2.5 h-5 w-5 text-eh-muted" />
                <button
                  type="button"
                  className="absolute right-3 top-2.5 text-eh-muted hover:text-eh-forest-ink"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {validationErrors.password && (
                <p className="mt-1 text-sm text-m2-error">{validationErrors.password}</p>
              )}
            </div>

            {accountLocked && !error && (
              <div className="rounded-md bg-m2-error/10 p-4">
                <div className="text-sm text-m2-error">
                  Tài khoản đã bị khóa do nghi ngờ chia sẻ. Vui lòng liên hệ quản trị viên để mở khóa.
                </div>
              </div>
            )}
            {error && (
              <div className="rounded-md bg-m2-error/10 p-4">
                <div className="text-sm text-m2-error">{error}</div>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`eh-btn w-full ${
                  isLoading ? 'cursor-not-allowed opacity-60' : ''
                }`}
              >
                {isLoading ? (
                  <>
                    <svg className="mr-2 h-4 w-4 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden>
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Đang đăng nhập...
                  </>
                ) : (
                  'Đăng nhập'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-eh-border" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-eh-cream px-2 text-eh-muted">Hoặc</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/register"
                className="eh-btn-secondary w-full"
              >
                Tạo tài khoản mới
              </Link>
            </div>
          </div>
        </div>
      </div>

      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />
    </div>
  );
};

export default LoginPage;
